import React, { useEffect } from 'react';
import { throttle } from 'lodash';
import { getWindow } from '../../utils/common';

/** @type {Record<"XS" | "SM" | "MD" | "LG" | "XL", "XS" | "SM" | "MD" | "LG" | "XL">} */
const BREAK_POINTS = {
    XS: 'XS',
    SM: 'SM',
    MD: 'MD',
    LG: 'LG',
    XL: 'XL'
};

/** @type {Partial<Record<keyof BREAK_POINTS, number>>} */
const BREAK_POINT_WIDTH = {
    [BREAK_POINTS.SM]: 600,
    [BREAK_POINTS.MD]: 900,
    [BREAK_POINTS.LG]: 1160,
    [BREAK_POINTS.XL]: 1760
};

/**
 * @param {number} width
 * @returns {Array<keyof BREAK_POINTS>}
 */
const matchBreakpoints = (width) => {
    if (width < BREAK_POINT_WIDTH.SM) {
        return [BREAK_POINTS.XS];
    } else if (width >= BREAK_POINT_WIDTH.SM && width < BREAK_POINT_WIDTH.MD) {
        return [BREAK_POINTS.XS, BREAK_POINTS.SM];
    } else if (width >= BREAK_POINT_WIDTH.MD && width < BREAK_POINT_WIDTH.LG) {
        return [BREAK_POINTS.XS, BREAK_POINTS.SM, BREAK_POINTS.MD];
    } else if (width >= BREAK_POINT_WIDTH.LG && width < BREAK_POINT_WIDTH.XL) {
        return [BREAK_POINTS.XS, BREAK_POINTS.SM, BREAK_POINTS.MD, BREAK_POINTS.LG];
    } else if (width >= BREAK_POINT_WIDTH.XL) {
        return [BREAK_POINTS.XS, BREAK_POINTS.SM, BREAK_POINTS.MD, BREAK_POINTS.LG, BREAK_POINTS.XL];
    }
};

/**
 * @param {number} width
 * @returns {import('types/hooks/common').UseBreakpointsReturn}
 */
const buildState = (width) => {
    const activePoints = width ? matchBreakpoints(width) : [];

    // @ts-ignore
    return Object.values(BREAK_POINTS).reduce((acc, point) => {
        acc[`is${point}`] = activePoints.includes(point);
        return acc;
    }, {});
};

/**
 * @type {import('types/hooks/common').useBreakpoints}
 */
const useBreakpoints = (wrapperWidth = 0) => {
    const [state, setState] = React.useState(buildState(wrapperWidth || getWindow()?.innerWidth));

    useEffect(() => {
        const calcInnerWidth = throttle(() => {
            setState(buildState(wrapperWidth || getWindow()?.innerWidth));
        }, 200);

        getWindow()?.addEventListener('resize', calcInnerWidth);
        getWindow()?.addEventListener('load', calcInnerWidth);

        return () => {
            getWindow()?.removeEventListener('resize', calcInnerWidth);
            getWindow()?.removeEventListener('load', calcInnerWidth);
        }
    }, [wrapperWidth]);

    useEffect(() => {
        if (wrapperWidth) {
            setState(buildState(wrapperWidth));
        }
    }, [wrapperWidth]);

    return state;
};

export default useBreakpoints;
